// Columna de filtros del explorer: encabezado (título, conteo, limpiar, colapsar) y riel de 40px
// al plegarse. Los filtros de cada pantalla entran como `children`.
import type { ReactNode } from 'react';
import { ChevronLeft, Filter, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/cn';

const ICON_BUTTON =
    'flex items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50';

/** Medidas de los campos de la columna (Clientes, Cartera, Cuotas): rótulo, grupo, campo y select nativo. */
export const FILTER_LABEL =
    'mb-[5px] block text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
export const FILTER_GROUP = 'mt-2.5';
export const FILTER_FIELD =
    'h-[26px] w-full rounded-md border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50';
export const FILTER_NOTE = 'mt-[3px] text-[9.5px] leading-snug text-muted-foreground';

/** Rótulo accesible del conteo: «1 filtro activo», «3 filtros activos». */
export function activeFiltersAriaLabel(count: number): string {
    const plural = count === 1 ? '' : 's';
    return `${String(count)} filtro${plural} activo${plural}`;
}

export interface FiltersColumnProps {
    /** Los grupos de filtros de la pantalla; van dentro del cuerpo con scroll propio. */
    children: ReactNode;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    activeCount: number;
    onReset: () => void;
    resetDisabled?: boolean;
    title?: string;
    /** Marca extra junto al conteo, p. ej. «Sobre lo cargado». */
    titleAdornment?: ReactNode;
    bodyClassName?: string;
    /** Con él la columna es una región con nombre, para cuando sus rótulos se repiten en la tabla. */
    regionLabel?: string;
}

export function FiltersColumn({ regionLabel, ...props }: FiltersColumnProps) {
    if (regionLabel === undefined) return <FiltersColumnContent {...props} />;
    return (
        <div role="region" aria-label={regionLabel} className="flex min-h-0 flex-1 flex-col">
            <FiltersColumnContent {...props} />
        </div>
    );
}

function FiltersColumnContent({
    children,
    collapsed,
    onToggleCollapsed,
    activeCount,
    onReset,
    resetDisabled = false,
    title = 'Filtros',
    titleAdornment,
    bodyClassName,
}: Omit<FiltersColumnProps, 'regionLabel'>) {
    const countLabel = activeFiltersAriaLabel(activeCount);

    if (collapsed) {
        return (
            <div className="flex flex-col items-center gap-1.5 border-b border-border px-1.5 py-2.5">
                <button
                    type="button"
                    onClick={onToggleCollapsed}
                    title="Expandir filtros"
                    aria-label="Expandir filtros"
                    aria-expanded={false}
                    className={cn(ICON_BUTTON, 'size-5')}
                >
                    <ChevronLeft className="size-3 rotate-180" aria-hidden />
                </button>
                {activeCount > 0 ? (
                    <span
                        role="status"
                        aria-label={countLabel}
                        title={countLabel}
                        className="flex size-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold tabular-nums text-primary-foreground"
                    >
                        {activeCount}
                    </span>
                ) : null}
            </div>
        );
    }

    return (
        <>
            <div className="flex min-h-10 flex-none items-center justify-between border-b border-border px-3 py-2">
                <div className="flex items-center gap-2">
                    <Filter className="size-3.5 text-foreground" aria-hidden />
                    <span className="text-[12px] font-semibold">{title}</span>
                    <span
                        aria-label={countLabel}
                        className="rounded-full bg-muted px-[5px] text-[9px] font-semibold text-muted-foreground"
                    >
                        {activeCount}
                    </span>
                    {titleAdornment}
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={onReset}
                        disabled={resetDisabled}
                        title="Limpiar filtros"
                        aria-label="Limpiar filtros"
                        className={cn(ICON_BUTTON, 'h-[22px] px-1.5')}
                    >
                        <RotateCcw className="size-3" aria-hidden />
                    </button>
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        title="Colapsar filtros"
                        aria-label="Colapsar filtros"
                        aria-expanded
                        className={cn(ICON_BUTTON, 'size-5')}
                    >
                        <ChevronLeft className="size-3" aria-hidden />
                    </button>
                </div>
            </div>

            <div
                className={cn(
                    'min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-1 text-[12px]',
                    bodyClassName,
                )}
            >
                {children}
            </div>
        </>
    );
}
